<template>
  <!-- Заголовок страницы с кнопкой создания -->
  <UPageSection
    class="main-page"
    title="Ваши заметки"
    description="На этом экране можно управлять заметками"
  >
    <template #links
      ><UButton
        icon="i-lucide-plus"
        size="xl"
        variant="outline"
        @click="createNote()"
      >
        Создать заметку
      </UButton>
    </template>

    <!-- Индикатор загрузки -->
    <Loader v-if="loading" class="main-page__loader"></Loader>

    <!-- Сетка с карточками заметок -->
    <UPageGrid v-else class="main-page__grid">
      <TodoCard
        v-for="(list, idx) in lists"
        :key="idx"
        v-bind="list"
        @delete="handleRemove"
        @edit="(id) => router.push(`/notes/${id}`)"
      />
    </UPageGrid>
  </UPageSection>

  <!-- Модальное окно подтверждения удаления -->
  <ConfirmRemoveModal
    v-model="showModal"
    @confirm="handleConfirmRemove"
    @cancel="handleCloseModal"
  />
</template>

<script setup lang="ts">
import { useToDoStore } from "@/stores/todo";
import { v4 as uuidv4 } from "uuid";

// Получаем роутер для навигации
const router = useRouter();

// Работа с todo store
const toDoStore = useToDoStore();

// Используем computed для реактивности списков
const lists = computed(() => toDoStore.lists);

// Состояние загрузки
const loading = ref(true);

// Состояние модального окна
const showModal = ref(false);
const idToDelete = ref<string | null>(null);

// Загружаем данные при монтировании компонента
onMounted(() => {
  toDoStore.loadFromLocalStorage();
  filterEmptyItems();
  loading.value = false;
});

/**
 * Создает новую заметку и переходит к ее редактированию
 */
const createNote = () => {
  const newListId = uuidv4();
  router.push(`/notes/${newListId}`);
};

/**
 * Обрабатывает запрос на удаление заметки (открывает модальное окно)
 * @param id - ID заметки для удаления
 */
const handleRemove = (id: string) => {
  showModal.value = true;
  idToDelete.value = id;
};

/**
 * Закрывает модальное окно подтверждения
 */
const handleCloseModal = () => {
  showModal.value = false;
  idToDelete.value = null;
};

/**
 * Подтверждает удаление заметки
 */
const handleConfirmRemove = () => {
  if (idToDelete.value) {
    toDoStore.removeList(idToDelete.value);
  }
  handleCloseModal();
};

const filterEmptyItems = () => {
  toDoStore.lists = lists.value.filter(
    (list) => Array.isArray(list.items) && list.items.length > 0,
  );
};
</script>

<style lang="scss">
@use "@/assets/base-scss" as *;
.main-page {
  display: flex;
  flex-direction: column;

  @include spacing-vertical-sm(gap);

  &__loader {
    @include spacing-vertical-lg(padding);
  }
  &__grid {
    grid-auto-rows: 1fr;
  }
}
</style>
